'use client'

import { useEffect } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { useProductAnalytics } from '../hooks/useProductAnalytics'
import { sanitizeAnalyticsPath } from '../lib/analytics-sanitize'
import type { AnalyticsEventName, AnalyticsRole } from '../lib/product-analytics'

function roleForPath(pathname: string): AnalyticsRole {
  if (pathname.startsWith('/admin')) return 'super_admin'
  if (pathname.startsWith('/moderator')) return 'moderator'
  if (pathname.includes('/waqf')) return 'waqf_officer'
  if (pathname.startsWith('/volunteer')) return 'volunteer'
  if (pathname.includes('/committee')) return 'committee'
  if (pathname.includes('/donation') || pathname.includes('/donate') || pathname.includes('/campaign')) return 'donor'
  return 'anonymous'
}

function eventForPath(pathname: string): AnalyticsEventName {
  if (pathname === '/onboard') return 'onboarding_started'
  if (pathname.includes('/dashboard')) return 'dashboard_activity'
  if (pathname === '/notifications') return 'notification_opened'
  if (pathname === '/ai') return 'ai_assistant_used'
  if (pathname === '/search') return 'search_performed'
  if (pathname.includes('/waqf')) return 'waqf_interaction'
  return 'page_view'
}

export default function ProductAnalyticsTracker() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const track = useProductAnalytics()

  useEffect(() => {
    // The private /verify surface is EXCLUDED from product analytics entirely (belt-and-suspenders on
    // top of sanitizeAnalyticsPath, which already redacts the token) — no event is sent for it at all.
    if (pathname.startsWith('/verify/')) return
    /* PATH: pathname + ALLOWLISTED params only — never the raw query string.
       /reset-password carries a credential-equivalent `?token=…`, so an unfiltered path
       would persist a live reset token into analytics_events (and surface it in
       /admin/analytics "Top pages"). Unknown params fail CLOSED (see analytics-sanitize).
       metadata.query is gone entirely — we don't even send it. */
    const query = searchParams?.toString()
    track({
      name: eventForPath(pathname),
      role: roleForPath(pathname),
      path: sanitizeAnalyticsPath(query ? `${pathname}?${query}` : pathname) || pathname,
    })
  }, [pathname, searchParams, track])

  return null
}
